<template>
  <div class="auth-page">
    <div class="container auth-page__container">
      <div class="auth-layout">
        <aside class="auth-layout__panel hp-hide-mobile">
          <p class="auth-layout__eyebrow">Welcome back</p>
          <h2 class="auth-layout__title">Sign in to find jobs &amp; manage your visume</h2>
          <p class="auth-layout__text">
            Access your profile, apply to openings, and keep your 2-minute video resume up to date.
          </p>
          <ul class="auth-layout__points">
            <li>Apply to fresher, IT &amp; government jobs</li>
            <li>Track applications from your dashboard</li>
            <li>Update your video profile anytime</li>
          </ul>
        </aside>

        <div class="auth-card">
          <header class="auth-card__header">
            <h1 class="auth-card__title">Jobseeker login</h1>
            <p class="auth-card__subtitle">Sign in with your registered email</p>
          </header>

          <div
            v-if="contextAlert"
            class="auth-alert auth-alert--success"
            role="status"
          >
            <p class="auth-alert__title">{{ contextAlert.title }}</p>
            <p v-if="contextAlert.subtitle" class="auth-alert__text">{{ contextAlert.subtitle }}</p>
          </div>

          <div v-if="reset_success" class="auth-alert auth-alert--success" role="status">
            <p class="auth-alert__title">Password has been changed. Please login with your new password.</p>
          </div>

          <v-form ref="login_form" v-model="valid" lazy-validation class="auth-form">
            <div class="auth-form__field">
              <v-text-field
                type="text"
                v-model="login.email"
                :rules="[v => !!v || 'Enter valid email']"
                :error-messages="email_error"
                @keydown.space.prevent
                outlined
                dense
                hide-details="auto"
              >
                <template #label>Email<span class="red--text"><strong>* </strong></span></template>
              </v-text-field>
            </div>

            <div class="auth-form__field">
              <v-text-field
                :append-icon="show1 ? $icons.eye : $icons.eyeOff"
                :type="show1 ? 'text' : 'password'"
                :error-messages="password_error"
                @click:append="show1 = !show1"
                v-model="login.password"
                :rules="[v => !!v || 'Enter valid password']"
                outlined
                dense
                hide-details="auto"
              >
                <template #label>Password<span class="red--text"><strong>* </strong></span></template>
              </v-text-field>
            </div>

            <div class="error-feedback" v-if="auth_error">{{ auth_msg }}</div>
            <div class="error-feedback" v-if="notverified_error">
              Email has not been verified.
              <span @click="sendVerification()">Click here to send verification</span>
            </div>
            <div class="success-feedback" v-if="success_msg">{{ success_msg }}</div>

            <div class="auth-form__links">
              <a href="/jobseeker/request_password">Forgot password?</a>
              <a v-if="write_to_admin" href="mailto:recruit@hireclap.com">Write to admin</a>
            </div>

            <div class="auth-form__submit">
              <button
                type="button"
                class="hp-btn hp-btn--primary"
                :disabled="loading"
                @click="userlogin()"
              >
                {{ loading ? 'Signing in…' : 'Login' }}
              </button>
            </div>
          </v-form>

          <footer class="auth-card__footer">
            <p class="auth-card__signup">
              Not a member?
              <a :href="registerHref">Create an account</a>
            </p>
            <p class="auth-card__legal">
              <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>
              ·
              <a href="/terms" target="_blank" rel="noopener">Terms &amp; Conditions</a>
            </p>
            <p class="auth-card__employer">
              Are you an employer?
              <a href="/recruit/register">Register free</a>
            </p>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import validation from '../libraries/validFormat';
import Actions from '../libraries/apiActions.js';
import '../assets/hire.css';
import '../assets/home-page.css';
import '../assets/login-page.css';

const breadcrumbJson = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [{
    '@type': 'ListItem',
    position: 1,
    name: 'Home',
    item: window.location.origin,
  }, {
    '@type': 'ListItem',
    position: 2,
    name: 'Login',
    item: window.location.href,
  }],
};

export default {
  props: ['visume'],
  data() {
    return {
      validate: new validation(),
      action: new Actions(),
      login: {
        email: null,
        password: null,
      },
      errors: [],
      notVerifiedError: false,
      notverified_error: false,
      auth_error: false,
      auth_msg: '',
      can_login: 0,
      success_msg: false,
      showAlert: false,
      showDismissibleAlert: false,
      reset_success: false,
      write_to_admin: false,
      isMobile: false,
      show1: false,
      valid: true,
      email_error: null,
      password_error: null,
      loading: false,
      tag_institute: false,
      tag_college: false,
      institute: null,
      college: null,
    };
  },
  metaInfo() {
    return {
      script: [
        { type: 'application/ld+json', json: breadcrumbJson },
      ],
    };
  },
  computed: {
    registerHref() {
      const { src, redirect_from: redirectFrom } = this.$route.query;
      const params = [];
      if (src) params.push(`src=${encodeURIComponent(src)}`);
      if (redirectFrom) params.push(`redirect_from=${encodeURIComponent(redirectFrom)}`);
      return params.length ? `/jobseeker/register?${params.join('&')}` : '/jobseeker/register';
    },
    contextAlert() {
      if (this.tag_institute) {
        return { title: 'Already registered', subtitle: 'Login to add training details' };
      }
      if (this.tag_college) {
        return { title: 'Already registered', subtitle: 'Login to tag college' };
      }
      if (this.showAlert) {
        return {
          title: 'Successfully registered!',
          subtitle: 'Sign in below. You can add your video profile anytime from your dashboard.',
        };
      }
      return null;
    },
  },
  beforeMount() {
    window.scrollTo(0, 0);
    this.reset_success = this.$route.query.reset_success || false;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      this.isMobile = true;
    }
    if (Object.keys(this.$route.params).length) {
      this.showDismissibleAlert = true;
      if (this.$route.params.institute) {
        this.institute = this.$route.params.institute;
        this.tag_institute = true;
      }
      if (this.$route.params.college) {
        this.college = this.$route.params.college;
        this.tag_college = true;
      }
      this.showAlert = true;
    } else {
      this.showDismissibleAlert = false;
    }
  },
  methods: {
    sendVerification() {
      this.notverified_error = false;
      if (this.tag_college && this.college) {
        this.login.college = this.college;
      }
      this.action.sendVerification(this.login).then((res) => {
        if (res.status) {
          this.success_msg = 'Email verification link send. Check your email & verify';
        } else {
          this.auth_error = true;
          this.auth_msg = res.error;
        }
      }).catch((err) => {
        if (err) {
          this.auth_error = true;
          this.auth_msg = err.error;
        }
      });
    },
    userlogin() {
      const src = this.$route.query.src;
      const redirect_from = this.$route.query.redirect_from;
      this.auth_error = false;
      this.success_msg = false;
      this.auth_msg = '';
      this.notverified_error = false;
      this.email_error = '';
      this.password_error = '';
      this.valid = this.$refs.login_form.validate();

      if (this.valid === true) {
        this.loading = true;
        if (src) {
          this.login.src = src;
        }
        new Actions()
          .seekerLogin(this.login)
          .then((res) => {
            this.loading = false;
            if (res.length && res[0].status === 1) {
              if (res[0].message === 'Email not registered with us') {
                this.email_error = 'Email not registered with us';
              }
            } else if (res.status === 2) {
              this.password_error = 'Password incorrect';
            } else if (res.email || res.password) {
              this.auth_error = true;
              this.auth_msg = 'Invalid Email or Password';
            } else if (res.message === 'Email not verified') {
              if (this.tag_college) {
                this.addCollege();
              }
              this.notverified_error = true;
            } else {
              const seeker = {
                token: res.token,
                name: res.name,
                visume_path: res.visume_path,
                resume_path: res.resume_path,
                id: res.id,
                alert: (!res.resume_path || !res.visume_path) ? 'yes' : 'no',
              };
              this.$cookies.set('hire-seeker', seeker);
              this.user = seeker;
              window.scrollTo(0, 0);
              if (this.tag_institute) {
                this.addTrainingDetails();
              }
              if (this.tag_college) {
                this.addCollege();
              }
              if (this.$route.query.redirect_from) {
                window.location.href = this.$route.query.redirect_from;
              } else if (src === 'seeker_job_description') {
                window.location.href = `/jobs/${redirect_from}`;
              } else {
                this.$router.replace('/myprofile');
                location.reload();
              }
            }
          })
          .catch((err) => {
            if (err) {
              this.loading = false;
              this.auth_error = true;
              if (window.navigator.onLine) {
                this.auth_msg = 'System error! Please try again.';
              } else {
                this.auth_msg = 'Network error! Please check your internet connection.';
              }
            }
          });
      } else {
        this.loading = false;
        this.auth_error = true;
        this.auth_msg = 'Required fields are empty!';
      }
    },
    addTrainingDetails: async function addTrainingDetails() {
      const token = this.$cookies.get('hire-seeker').token;
      await new Actions()
        .addUserTrainingDetails({ institute: this.institute }, token)
        .then(res => console.log(res));
    },
    addCollege: async function addCollege() {
      const token = this.$cookies.get('hire-seeker').token;
      await new Actions()
        .tagUserCollege({ college: this.college }, token)
        .then(res => console.log(res));
    },
  },
};
</script>
